import { useCallback, useEffect, useState, type FormEvent } from "react";
import Field from "../../components/Field";
import TextArea from "../../components/TextArea";
import Modal from "../../components/Modal";
import FormMessage from "../../components/FormMessage";
import Avatar from "../../components/Avatar";
import PhotoUpload from "../../components/PhotoUpload";
import { ApiError, apiFetch } from "../../lib/api";
import { useAuth } from "../../lib/auth";

type Rsvp = { userId: string; name: string; photoUrl: string | null };

type EventItem = {
  id: string;
  title: string;
  startsAt: string;
  location: string;
  description: string | null;
  coverUrl: string | null;
  isPast: boolean;
  rsvps: Rsvp[];
  goingCount: number;
  youAreGoing: boolean;
};

type Payload = { upcoming: EventItem[]; past: EventItem[] };

export default function EventsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<EventItem | null>(null);
  const [showPast, setShowPast] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const d = await apiFetch<Payload>("/api/events");
      setData(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load events.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function toggleRsvp(ev: EventItem) {
    try {
      if (ev.youAreGoing) {
        await apiFetch(`/api/events/${ev.id}/rsvp`, { method: "DELETE" });
      } else {
        await apiFetch(`/api/events/${ev.id}/rsvp`, { method: "POST" });
      }
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to RSVP.");
    }
  }

  async function remove(ev: EventItem) {
    if (!confirm(`Delete "${ev.title}"?`)) return;
    try {
      await apiFetch(`/api/events/${ev.id}`, { method: "DELETE" });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete.");
    }
  }

  return (
    <>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          gap: "1rem",
          flexWrap: "wrap",
          marginBottom: "2rem",
        }}
      >
        <div>
          <div className="eyebrow">Events</div>
          <h1
            className="display display--lg"
            style={{ marginTop: "0.5rem", marginBottom: 0 }}
          >
            in person.
          </h1>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setCreating(true)}
          >
            + New event
          </button>
        )}
      </header>

      {error && <FormMessage tone="error">{error}</FormMessage>}

      {!data ? (
        <p className="eyebrow">Loading…</p>
      ) : (
        <>
          {data.upcoming.length === 0 ? (
            <p style={{ color: "var(--c-palm)", opacity: 0.7 }}>
              No upcoming events yet.
            </p>
          ) : (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "grid",
                gap: "1.25rem",
              }}
            >
              {data.upcoming.map((ev) => (
                <EventCard
                  key={ev.id}
                  ev={ev}
                  isAdmin={isAdmin}
                  onRsvp={() => toggleRsvp(ev)}
                  onEdit={() => setEditing(ev)}
                  onDelete={() => remove(ev)}
                />
              ))}
            </ul>
          )}

          {data.past.length > 0 && (
            <section style={{ marginTop: "3rem" }}>
              <button
                type="button"
                onClick={() => setShowPast((s) => !s)}
                style={{
                  fontFamily: "var(--f-label)",
                  textTransform: "uppercase",
                  letterSpacing: "0.16em",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: "var(--c-palm)",
                  background: "transparent",
                  padding: "0.5rem 0",
                }}
              >
                {showPast ? "Hide" : "Show"} past events ({data.past.length})
              </button>
              {showPast && (
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "1rem 0 0",
                    display: "grid",
                    gap: "1rem",
                  }}
                >
                  {data.past.map((ev) => (
                    <EventCard
                      key={ev.id}
                      ev={ev}
                      isAdmin={isAdmin}
                      onRsvp={() => toggleRsvp(ev)}
                      onEdit={() => setEditing(ev)}
                      onDelete={() => remove(ev)}
                    />
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}

      {(creating || editing) && (
        <EventFormModal
          initial={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </>
  );
}

function EventCard({
  ev,
  isAdmin,
  onRsvp,
  onEdit,
  onDelete,
}: {
  ev: EventItem;
  isAdmin: boolean;
  onRsvp: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <li
      style={{
        background: "#fff",
        border: "1px solid var(--c-rule)",
        borderRadius: "var(--r-md)",
        padding: "1.25rem",
        opacity: ev.isPast ? 0.85 : 1,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          alignItems: "start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 250px", minWidth: 0 }}>
          <div className="eyebrow" style={{ color: "var(--c-coral)" }}>
            {new Date(ev.startsAt).toLocaleString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </div>
          <h3
            className="display"
            style={{
              fontSize: "clamp(1.4rem, 3vw, 1.85rem)",
              margin: "0.25rem 0 0.35rem",
              lineHeight: 1.1,
            }}
          >
            {ev.title.toLowerCase()}
          </h3>
          <div style={{ fontSize: "0.95rem", color: "var(--c-palm)" }}>
            📍 {ev.location}
          </div>
        </div>
        {!ev.isPast && (
          <button
            type="button"
            onClick={onRsvp}
            className={ev.youAreGoing ? "btn btn--ghost" : "btn btn--primary"}
            style={{ padding: "0.6rem 1.1rem", fontSize: "0.78rem" }}
          >
            {ev.youAreGoing ? "Going ✓" : "RSVP"}
          </button>
        )}
      </header>

      {ev.description && (
        <p style={{ marginTop: "0.85rem", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
          {ev.description}
        </p>
      )}

      <div
        style={{
          marginTop: "1rem",
          paddingTop: "0.85rem",
          borderTop: "1px solid var(--c-rule)",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <div
          className="eyebrow"
          style={{ color: "var(--c-palm)", fontSize: "0.7rem" }}
        >
          {ev.goingCount} going
        </div>
        <div style={{ display: "flex", gap: -8, alignItems: "center" }}>
          {ev.rsvps.slice(0, 6).map((r) => (
            <div
              key={r.userId}
              title={r.name}
              style={{ marginRight: -8 }}
            >
              <Avatar name={r.name} photoUrl={r.photoUrl} size={32} />
            </div>
          ))}
          {ev.rsvps.length > 6 && (
            <span
              style={{
                marginLeft: 6,
                fontSize: "0.85rem",
                color: "var(--c-palm)",
              }}
            >
              +{ev.rsvps.length - 6}
            </span>
          )}
        </div>
        {isAdmin && (
          <div style={{ marginLeft: "auto", display: "flex", gap: "0.4rem" }}>
            <button
              type="button"
              onClick={onEdit}
              className="btn btn--ghost"
              style={{ padding: "0.4rem 0.8rem", fontSize: "0.7rem" }}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              style={{
                padding: "0.4rem 0.8rem",
                fontSize: "0.7rem",
                fontFamily: "var(--f-label)",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontWeight: 700,
                color: "var(--c-coral)",
                background: "transparent",
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

function EventFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial?: EventItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [startsAt, setStartsAt] = useState(
    initial?.startsAt ? new Date(initial.startsAt).toISOString().slice(0, 16) : "",
  );
  const [location, setLocation] = useState(initial?.location ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [coverUrl, setCoverUrl] = useState<string | null>(
    initial?.coverUrl ?? null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        title: title.trim(),
        startsAt: new Date(startsAt).toISOString(),
        location: location.trim(),
        description: description.trim() || null,
        coverUrl: coverUrl,
      };
      if (initial) {
        await apiFetch(`/api/events/${initial.id}`, { method: "PATCH", body });
      } else {
        await apiFetch("/api/events", { method: "POST", body });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? "Edit event" : "New event"}
      maxWidth={620}
    >
      {error && <FormMessage tone="error">{error}</FormMessage>}
      <form onSubmit={onSubmit} noValidate>
        <Field
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <Field
          label="Date & time"
          type="datetime-local"
          required
          value={startsAt}
          onChange={(e) => setStartsAt(e.currentTarget.value)}
        />
        <Field
          label="Location"
          required
          value={location}
          onChange={(e) => setLocation(e.currentTarget.value)}
          hint='e.g. "My backyard, 123 Main St" or "Annie’s Cafe".'
        />
        <TextArea
          label="Description"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          maxLength={4000}
        />
        <PhotoUpload
          label="Cover image"
          value={coverUrl}
          onChange={setCoverUrl}
          shape="rect"
          aspectRatio="16/9"
          previewSize={200}
        />
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            justifyContent: "flex-end",
            marginTop: "0.5rem",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn btn--ghost"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
